import { useTheme } from '@mui/material/styles'
import {
  DisplayModeIcon,
  MenuFirstDayIcon,
  MenuIcon,
  MenuLanguageIcon
} from 'assets/icons/menu'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import {
  APP_THEME_MODES,
  AppThemeMode,
  useAppSettings
} from 'components/providers/AppSettingsProvider'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { updateUserSettings } from 'services/api/userSettings'
import Database from 'services/db/Database'
import { AccountDoc, UserSettingsDoc } from 'types/api/changes'
import { Model } from 'types/model'

import PickLanguagePopup, { langs } from '../PickLanguagePopup'
import SelectItemWithSearchPopup from '../SelectItemWithSearchPopup'
import { days } from './UserMenuPopup'
import DefaultCalendarSection from './DefaultCalendarSection'

export interface SettingsSectionProps {
  account?: Model<AccountDoc>
  userSettings: Model<UserSettingsDoc> | undefined
  setMenuOpen: (b: boolean) => void
}

export default function SettingsSection(props: SettingsSectionProps) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const appSettings = useAppSettings()

  const [showLanguagePopup, onShowLanguagePopup] = useState(false)
  const [showFirstDayPopup, onShowFirstDayPopup] = useState(false)
  const [showThemePopup, onShowThemePopup] = useState(false)

  const showLangauge = () => {
    onShowLanguagePopup(true)
  }

  const onLangChanged = (lang: string) => {
    window.location.reload()
    i18n.changeLanguage(lang)
  }

  const showFirstDay = () => {
    onShowFirstDayPopup(true)
  }

  const showThemeSelector = () => {
    onShowThemePopup(true)
  }

  const themeOptions = [
    { id: APP_THEME_MODES.SYSTEM, title: t('system') },
    { id: APP_THEME_MODES.LIGHT, title: t('light') },
    { id: APP_THEME_MODES.DARK, title: t('dark') }
  ]

  return (
    <PopupSection title={`${t('settings')}`} popupSectionGap={1}>
      <PickLanguagePopup
        open={showLanguagePopup}
        setOpen={onShowLanguagePopup}
        selectItem={onLangChanged}
      >
        <span onClick={showLangauge}>
          <PopupSectionRow
            cursor="pointer"
            title={`${t('language')}`}
            iconClass="icon-regular"
            svgHeight="20px"
            maxWidthIcon={20}
            actionBg
            engulfEntireRow
            icon={<MenuLanguageIcon style={{ cursor: 'default' }} />}
            value={
              langs.find((lang) => i18n.language.includes(lang.lang))?.name
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </PickLanguagePopup>
      <SelectItemWithSearchPopup
        open={showFirstDayPopup}
        setOpen={onShowFirstDayPopup}
        selectItem={(item) => {
          const userSettings = Database.toServerObject<UserSettingsDoc>(
            props.userSettings
          ) as UserSettingsDoc
          userSettings.FirstDayOfTheWeek = Number(item.id ?? 1)
          updateUserSettings(userSettings).then((updated) => {
            appSettings.setFirstDayOfTheWeek(userSettings.FirstDayOfTheWeek)
            props.setMenuOpen(false)
          })
        }}
        data={days}
        selectedItemId={`${appSettings.firstDayOfTheWeek}`}
      >
        <span id="first-day-button" onClick={showFirstDay}>
          <PopupSectionRow
            title={`${t('firstDay')}`}
            iconClass="icon-regular"
            maxWidthIcon={20}
            cursor="pointer"
            actionBg
            engulfEntireRow
            icon={<MenuFirstDayIcon style={{ cursor: 'default' }} />}
            value={
              days.find((day) => day.id === `${appSettings.firstDayOfTheWeek}`)
                ?.title
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </SelectItemWithSearchPopup>
      {props.account && (
        <DefaultCalendarSection {...props} account={props.account} />
      )}
      <SelectItemWithSearchPopup
        open={showThemePopup}
        setOpen={onShowThemePopup}
        selectItem={(item) => {
          onShowThemePopup(false)
          props.setMenuOpen(false)
          const mode = item.id as AppThemeMode

          if (
            mode === APP_THEME_MODES.SYSTEM ||
            mode === APP_THEME_MODES.LIGHT ||
            mode === APP_THEME_MODES.DARK
          ) {
            appSettings.setThemeMode(mode)
          }
        }}
        data={themeOptions}
        selectedItemId={appSettings.themeMode}
      >
        <span onClick={showThemeSelector}>
          <PopupSectionRow
            title={`${t('theme')}`}
            iconClass="icon-regular"
            maxWidthIcon={20}
            cursor="pointer"
            actionBg
            engulfEntireRow
            icon={<DisplayModeIcon style={{ cursor: 'default' }} />}
            value={
              themeOptions.find((item) => item.id === appSettings.themeMode)
                ?.title
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </SelectItemWithSearchPopup>
    </PopupSection>
  )
}
